import React, { useCallback, useEffect, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, Paper } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';

import { getSales } from '../../services/salesService';
import DataGridTable from '../table/DataGridTable';
import EmptyList from '../misc/EmptyList';
import RefreshButton from '../misc/RefreshButton';
import BulkRefundActions from '../sales/BulkRefundActions';
import EventListSearchBar from '../events/EventListSearchBar';
import { SALES_TABLE } from '../../definitions';
import {
  checkSalesPermissions,
  getSalesPageTitle,
  getManagerColumns,
  resolveSalePaymentId,
  getSaleDetailHref,
} from '../../utils/salesUtils';
import { getPaymentIdBySale } from '../../services/paymentsService';
import { gridFilterModelToSalesTicketsQuery } from '../../utils/salesTicketsGridFilters';

/**
 * Shared sales grid — used by /sales page and event edit Satışlar tab.
 * @param {{ eventID?: string, stockID?: string, userID?: string, embedded?: boolean, hideTitle?: boolean }} props
 */
export const SalesPanel = ({
  eventID = null,
  stockID = null,
  userID = null,
  embedded = false,
  hideTitle = false,
}) => {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: SALES_TABLE?.defaults?.pageSize || 50,
  });
  const [allSalesForRefund, setAllSalesForRefund] = useState([]);
  const [columns, setColumns] = useState(SALES_TABLE.columns);
  const { permissions } = usePermissions();

  const { isManager, isOperator, isEditor, canView } = checkSalesPermissions(permissions);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const apiFilters = gridFilterModelToSalesTicketsQuery(filterModel, 'sales');
      const result = await getSales({
        userID,
        eventID,
        stockID,
        q: searchQ,
        apiFilters,
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
      });
      const rows = Array.isArray(result) ? result : (result?.data ?? []);
      const total = Array.isArray(result) ? rows.length : (result?.total ?? 0);
      setData(rows);
      setRowCount(total);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    userID,
    eventID,
    stockID,
    searchQ,
    filterModel,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  const fetchRefundSales = useCallback(async () => {
    if (!eventID) {
      setAllSalesForRefund([]);
      return;
    }
    try {
      const all = await getSales({
        userID,
        eventID,
        stockID,
        fetchAll: true,
      });
      setAllSalesForRefund(Array.isArray(all) ? all : (all?.data ?? []));
    } catch (error) {
      console.error('Error fetching sales for refund:', error);
      setAllSalesForRefund([]);
    }
  }, [userID, eventID, stockID]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [eventID, stockID]);

  useEffect(() => {
    if (!permissions) return;
    if (!embedded && isManager && !eventID) {
      window.location.href = '/#/events-v2';
      return;
    }
    if (isManager) {
      setColumns(getManagerColumns(SALES_TABLE.columns));
    }
    fetchData();
  }, [permissions, isManager, eventID, stockID, fetchData, embedded]);

  useEffect(() => {
    if (!permissions || !eventID) return;
    fetchRefundSales();
  }, [permissions, eventID, stockID, fetchRefundSales]);

  const handleFilterModelChange = (model) => {
    setFilterModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearch = (q) => {
    setSearchQ(q);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearSearch = () => {
    setSearchQ('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const CustomTableHeader = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <RefreshButton refreshData={fetchData} />
    </GridToolbarContainer>
  );

  const { title, description } = getSalesPageTitle(eventID, stockID, userID, translate);

  if (!permissions) {
    return <EmptyList mode="loading" />;
  }

  if (!embedded && isOperator) {
    window.location.href = '/#/events-v2';
    return null;
  }

  if (!embedded && isEditor) {
    window.location.href = '/#/news';
    return null;
  }

  if (!canView) {
    return (
      <EmptyList
        customMessage={translate('sales.no_permission', {
          _: "You don't have the right permission set to view this page. Please contact GişeKıbrıs if you believe this is an error.",
        })}
        noCreate={true}
      />
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {!hideTitle && (
        <Paper sx={{ p: embedded ? 2 : 3 }}>
          {!embedded && (
            <>
              <Typography variant="h4" gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </>
          )}
          <EventListSearchBar
            value={searchQ}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            disabled={isLoading}
            placeholderKey={userID ? 'sales.search_placeholder_user' : 'sales.search_placeholder'}
          />
          {eventID && (
            <BulkRefundActions
              eventID={eventID}
              sales={allSalesForRefund}
              onDone={() => {
                fetchData();
                fetchRefundSales();
              }}
            />
          )}
        </Paper>
      )}

      {hideTitle && (
        <Box sx={{ mb: 1 }}>
          <EventListSearchBar
            value={searchQ}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            disabled={isLoading}
            placeholderKey={userID ? 'sales.search_placeholder_user' : 'sales.search_placeholder'}
          />
          {eventID && (
            <BulkRefundActions
              eventID={eventID}
              sales={allSalesForRefund}
              onDone={() => {
                fetchData();
                fetchRefundSales();
              }}
            />
          )}
        </Box>
      )}

      <Paper
        sx={{
          flex: 1,
          p: 2,
          mt: hideTitle ? 0 : 2,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridTable
          config={SALES_TABLE}
          rows={data}
          columns={columns}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          autoHeight={false}
          onRowClick={async (params, event) => {
            if (event?.target?.closest?.('a,button')) return;
            const row = params?.row || {};
            let paymentId = resolveSalePaymentId(row);
            if (!paymentId && row.id) {
              paymentId = await getPaymentIdBySale(row.id);
            }
            window.location.href = getSaleDetailHref(row, paymentId);
          }}
          onColumnsVisibilityChange={(model) => {
            setColumns(
              columns.map((column) => ({
                ...column,
                hide: !model[column.field],
              })),
            );
          }}
          sx={{
            height: embedded ? 480 : 'calc(100vh - 280px)',
            minHeight: 360,
            '.MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
          slots={{ toolbar: CustomTableHeader }}
        />
      </Paper>
    </Box>
  );
};

export default SalesPanel;
